/**
 * KnowledgeTab Component
 *
 * Displays and manages knowledge cards for a contact or company.
 * Shows expertise, facts, AI insights, relationships, and manual notes.
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { contactsApi, companiesApi } from '@/services/contactsApi';
import type { KnowledgeCard, ContactEmailInsights } from '../types';

interface SuppressedItem {
  item_type: string;
  item_value: string;
}

interface KnowledgeItem {
  id: string;
  content: string;
  source: 'email_insights' | 'knowledge_card';
}

interface KnowledgeTabProps {
  entityId: string;
  entityType: 'contact' | 'company';
  cards: KnowledgeCard[];
  insights?: ContactEmailInsights | null;
  loading: boolean;
  onRefresh: () => void;
  onOpenSuppressionModal: (
    itemType: 'expertise' | 'fact',
    content: string,
    id: string,
    source: 'email_insights' | 'knowledge_card'
  ) => void;
  onError: (message: string) => void;
  suppressedItems?: SuppressedItem[];
}

export default function KnowledgeTab({
  entityId,
  entityType,
  cards,
  insights,
  loading,
  onRefresh,
  onOpenSuppressionModal,
  onError,
  suppressedItems = [],
}: KnowledgeTabProps) {
  const { t } = useTranslation('people');
  // Convert suppressedItems array to Set for efficient lookup
  const suppressedSet = new Set(
    suppressedItems.map(item => `${item.item_type}:${item.item_value}`)
  );

  // Internal handlers that call onRefresh after mutations
  const handleDeleteCard = useCallback(async (cardId: string) => {
    try {
      const api = entityType === 'contact' ? contactsApi : companiesApi;
      await api.deleteKnowledgeCard(entityId, cardId);
      onRefresh();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to delete card');
    }
  }, [entityId, entityType, onRefresh, onError]);

  const handleAddCard = useCallback(async (cardType: 'note' | 'topic' | 'fact', content: string) => {
    try {
      const api = entityType === 'contact' ? contactsApi : companiesApi;
      await api.addNote(entityId, { card_type: cardType, content });
      onRefresh();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to add card');
    }
  }, [entityId, entityType, onRefresh, onError]);
  const [newNote, setNewNote] = useState('');
  const [newExpertise, setNewExpertise] = useState('');
  const [newFact, setNewFact] = useState('');
  const [showAddExpertise, setShowAddExpertise] = useState(false);
  const [showAddFact, setShowAddFact] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Group cards by type
  const aiInsights = cards.filter(c => c.source === 'ai_generated' && c.card_type === 'insight');
  const relationships = cards.filter(c => c.card_type === 'relationship');
  const manualNotes = cards.filter(c => c.source === 'manual' && c.card_type === 'note');
  const topicCards = cards.filter(c => c.card_type === 'topic');
  const factCards = cards.filter(c => c.card_type === 'fact');

  // Combine expertise from email insights and topic cards
  const allExpertise: KnowledgeItem[] = [
    ...(insights?.expertise || [])
      .filter(e => !suppressedSet.has(`expertise:${e}`))
      .map((e, i) => ({ id: `ei-${i}`, content: e, source: 'email_insights' as const })),
    ...topicCards.map(c => ({ id: c.id, content: c.content, source: 'knowledge_card' as const })),
  ];

  // Combine facts from email insights and fact cards
  const allFacts: KnowledgeItem[] = [
    ...(insights?.facts || [])
      .filter(f => !suppressedSet.has(`fact:${f}`))
      .map((f, i) => ({ id: `ef-${i}`, content: f, source: 'email_insights' as const })),
    ...factCards.map(c => ({ id: c.id, content: c.content, source: 'knowledge_card' as const })),
  ];

  // Add note handler
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    await handleAddCard('note', newNote.trim());
    setNewNote('');
  };

  // Add expertise handler
  const handleAddExpertise = async () => {
    if (!newExpertise.trim()) return;
    await handleAddCard('topic', newExpertise.trim());
    setNewExpertise('');
    setShowAddExpertise(false);
  };

  // Add fact handler
  const handleAddFact = async () => {
    if (!newFact.trim()) return;
    await handleAddCard('fact', newFact.trim());
    setNewFact('');
    setShowAddFact(false);
  };

  return (
    <div className="space-y-4">
      {/* Refresh Insights Button */}
      <div className="flex justify-end">
        <button
          onClick={onRefresh}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {t('knowledge_tab.refresh_ai_insights')}
        </button>
      </div>

      {/* Expertise Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
          <span className="text-purple-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </span>
          {t('knowledge_tab.expertise')}
          <span className="text-xs text-gray-400 font-normal">({allExpertise.length})</span>
        </h4>
        <div className="flex flex-wrap gap-2">
          {allExpertise.map((item) => (
            <span
              key={item.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 text-sm rounded hover:bg-purple-100 transition-colors"
            >
              <button
                onClick={() => onOpenSuppressionModal('expertise', item.content, item.id, item.source)}
                className="text-purple-400 hover:text-red-500 transition-colors"
                title={item.source === 'email_insights' ? t('knowledge_tab.suppress_tooltip') : t('knowledge_tab.delete_tooltip')}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <span className="text-xs" title={item.source === 'email_insights' ? t('knowledge_tab.ai_extracted') : t('knowledge_tab.manually_added')}>
                {item.source === 'email_insights' ? '🤖' : '📌'}
              </span>
              {item.content}
            </span>
          ))}
          {/* Add Expertise */}
          {showAddExpertise ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newExpertise}
                onChange={(e) => setNewExpertise(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newExpertise.trim()) handleAddExpertise();
                  if (e.key === 'Escape') { setShowAddExpertise(false); setNewExpertise(''); }
                }}
                placeholder={t('knowledge_tab.add_expertise_placeholder')}
                className="px-2 py-1 text-sm border border-purple-300 rounded focus:ring-2 focus:ring-purple-200 focus:border-purple-500"
                autoFocus
              />
              <button
                onClick={handleAddExpertise}
                disabled={!newExpertise.trim()}
                className="px-2 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
              >
                {t('knowledge_tab.add')}
              </button>
              <button
                onClick={() => { setShowAddExpertise(false); setNewExpertise(''); }}
                className="px-2 py-1 text-sm text-gray-500 hover:text-gray-700"
              >
                {t('knowledge_tab.cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddExpertise(true)}
              className="inline-flex items-center gap-1 px-2 py-1 border border-dashed border-purple-300 text-purple-600 text-sm rounded hover:bg-purple-50 transition-colors"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('knowledge_tab.add')}
            </button>
          )}
        </div>
        {allExpertise.length === 0 && !showAddExpertise && (
          <p className="text-sm text-gray-400 mt-2">{t('knowledge_tab.no_expertise')}</p>
        )}
      </div>

      {/* Known Facts Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
          <span className="text-blue-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
          {t('knowledge_tab.known_facts')}
          <span className="text-xs text-gray-400 font-normal">({allFacts.length})</span>
        </h4>
        <div className="space-y-2">
          {allFacts.map((item) => (
            <div key={item.id} className="flex items-start gap-2 text-sm">
              <button
                onClick={() => onOpenSuppressionModal('fact', item.content, item.id, item.source)}
                className="text-gray-400 hover:text-red-500 transition-colors p-1 flex-shrink-0 -ml-1"
                title={item.source === 'email_insights' ? t('knowledge_tab.suppress_tooltip') : t('knowledge_tab.delete_tooltip')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <span className="text-blue-400 mt-0.5" title={item.source === 'email_insights' ? t('knowledge_tab.ai_extracted') : t('knowledge_tab.manually_added')}>
                {item.source === 'email_insights' ? '🤖' : '📌'}
              </span>
              <p className="text-gray-700 flex-1">{item.content}</p>
            </div>
          ))}
          {/* Add Fact */}
          {showAddFact ? (
            <div className="mt-2 space-y-2">
              <input
                type="text"
                value={newFact}
                onChange={(e) => setNewFact(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newFact.trim()) handleAddFact();
                  if (e.key === 'Escape') { setShowAddFact(false); setNewFact(''); }
                }}
                placeholder={t('knowledge_tab.add_fact_placeholder')}
                className="w-full px-3 py-2 text-sm border border-blue-300 rounded focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleAddFact}
                  disabled={!newFact.trim()}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {t('knowledge_tab.add_fact')}
                </button>
                <button
                  onClick={() => { setShowAddFact(false); setNewFact(''); }}
                  className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700"
                >
                  {t('knowledge_tab.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowAddFact(true)}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('knowledge_tab.add_fact')}
            </button>
          )}
        </div>
        {allFacts.length === 0 && !showAddFact && (
          <p className="text-sm text-gray-400 mt-2">{t('knowledge_tab.no_facts')}</p>
        )}
      </div>

      {/* AI-Generated Insights Section */}
      {aiInsights.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
            <span className="text-orange-500">🤖</span>
            {t('knowledge_tab.ai_generated_insights')}
            <span className="text-xs text-gray-400 font-normal ml-auto">{t('knowledge_tab.click_to_remove')}</span>
          </h4>
          <div className="space-y-2">
            {aiInsights.map((card) => (
              <div key={card.id} className="flex items-start gap-2 text-sm group bg-orange-50 rounded-lg p-2">
                <div className="flex-1">
                  <p className="text-gray-700">{card.content}</p>
                  {card.confidence && (
                    <span className="text-xs text-gray-400">
                      {Math.round(card.confidence * 100)}% confidence
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    if (confirm(t('knowledge_tab.delete_insight_confirmation'))) {
                      handleDeleteCard(card.id);
                    }
                  }}
                  className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all p-1"
                  title={t('knowledge_tab.delete_tooltip')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Relationships Section */}
      {relationships.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
            <span className="text-green-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </span>
            {t('knowledge_tab.relationships')}
          </h4>
          <div className="space-y-2">
            {relationships.map((card) => (
              <div key={card.id} className="flex items-center gap-2 text-sm">
                <span className="text-green-400">•</span>
                <p className="text-gray-700">{card.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Manual Notes Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
          <span className="text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </span>
          {t('knowledge_tab.notes')}
        </h4>

        {/* Add Note Form */}
        <div className="mb-4">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder={t('knowledge_tab.write_note_placeholder', { entityType })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
          <div className="mt-2 flex justify-end">
            <button
              onClick={handleAddNote}
              disabled={!newNote.trim()}
              className="px-3 py-1.5 text-sm bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('knowledge_tab.add_note')}
            </button>
          </div>
        </div>

        {/* Existing Notes */}
        {manualNotes.length > 0 ? (
          <div className="space-y-3 border-t border-gray-100 pt-3">
            {manualNotes.map((card) => (
              <div key={card.id} className="bg-gray-50 rounded-lg p-3 group">
                <div className="flex items-start justify-between">
                  <p className="text-sm text-gray-700 flex-1">{card.content}</p>
                  <button
                    onClick={() => handleDeleteCard(card.id)}
                    className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all ml-2"
                    title={t('knowledge_tab.delete_note')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(card.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">
            {t('knowledge_tab.no_notes')}
          </p>
        )}
      </div>

      {/* Empty state when no cards at all */}
      {cards.length === 0 && !insights && (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <p className="font-medium">{t('knowledge_tab.empty_state.title')}</p>
          <p className="text-sm mt-1">{t('knowledge_tab.empty_state.description')}</p>
        </div>
      )}
    </div>
  );
}
